import { useEffect, useState } from 'react'
import Modal from '../../common/Modal'
import FileUploader from '../../common/FileUploader'
import { useFetch } from '../../../hooks/useCallApi'

const getInitials = (name) => {
  if (!name) return ''
  const parts = name.split(' ')
  return (
    (parts[0] ? parts[0][0] : '') +
    (parts.length > 1 ? parts[parts.length - 1][0] : '')
  ).toUpperCase()
}

const ProfileBadge = ({ initials, name, position }) => (
  <div className='flex items-center space-x-3 min-w-0'>
    <div className='flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold bg-gradient-to-r from-[#60A4F9] to-[#2865EC]'>
      {initials}
    </div>
    <div className='min-w-0'>
      <p className='text-gray-800 font-medium truncate'>{name}</p>
      <p className='text-sm text-gray-500 truncate'>{position}</p>
    </div>
  </div>
)

function ViewLegislationModal({ isOpen, onClose, legislation, type }) {
  const [legislationId, setLegislationId] = useState(null)

  useEffect(() => {
    if (legislation && isOpen) {
      const id =
        legislation?.id ??
        legislation?.legislation_id ??
        legislation?.legislative_document_id ??
        null

      setLegislationId(id)
    } else {
      setLegislationId(null)
    }
  }, [legislation, isOpen])

  const {
    data: legislationData,
    isLoading,
    isError,
    error,
  } = useFetch(
    ['legislation', legislationId],
    (api) =>
      api.admin.legislation.getLegislation(
        legislationId,
        {
          include: 'committee,sponsors,consultationRecords'
        }
      ),
    {
      enabled: !!legislationId
    }
  )

  if (!isOpen) return null

  const getStatusStyles = (status) => {
    if (!status) return 'bg-gray-100 text-gray-800'
    const normalized = (status || '').toLowerCase()
    switch (normalized) {
      case 'implemented':
      case 'active':
        return 'bg-blue-100 text-blue-800'
      case 'approved':
      case 'published':
        return 'bg-green-100 text-green-800'
      case 'pending':
      case 'draft':
        return 'bg-yellow-100 text-yellow-800'
      case 'archived':
      case 'inactive':
        return 'bg-gray-200 text-gray-700'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const formatStatus = (status) => {
    if (!status) return 'Unknown'
    return status
      .toString()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase())
  }

  const formatType = (type) => {
    if (!type) return '—'
    const normalized = type.toString().toLowerCase()
    return normalized.charAt(0).toUpperCase() + normalized.slice(1)
  }

  const responseData =
    legislationData?.data?.data ?? legislationData?.data ?? legislationData
  const data = responseData ?? legislation

  const formatRole = (role) => {
    if (!role) return ''
    return role
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  const structuredFields = {
    committee:
      data?.committee?.committee_name ??
      data?.committee?.name ??
      data?.committee_name ??
      (typeof data?.committee === 'string' ? data?.committee : null) ??
      'N/A',
    chairmanship: data?.committee?.chairman_name
      ? {
        name: data.committee.chairman_name,
        position: formatRole(data.committee.chairman_role) || 'Chairperson',
      }
      : data?.chairmanship ?? data?.chairman ?? null,
    authors: data?.sponsors ?? data?.authors ?? data?.main_sponsors ?? [],
    consultationRecords: data?.consultation_records ?? [],
    description:
      data?.action_description ?? data?.description ?? data?.content ?? 'N/A',
  }

  const legislationNumber =
    data?.doc_number ??
    data?.legislation_number ??
    data?.legislationNumber ??
    'N/A'

  const legislationType = formatType(
    data?.doc_type ?? data?.type ?? data?.legislationType
  )

  const status = formatStatus(data?.status)

  const formTitle = (
    <div className='flex items-center justify-between'>
      <div className='flex items-center space-x-3'>
        <div className='bg-gradient-to-r from-[#60A4F9] to-[#2865EC] px-3 py-1 rounded'>
          <h3 className='text-base text-white font-semibold'>
            {legislationType} No. {legislationNumber}
          </h3>
        </div>
        <span className='text-md font-semibold text-gray-800'>
          View Legislation
        </span>
      </div>
    </div>
  )

  const legislationFile = data?.legislative_file
    ? [
      {
        name: data?.legislative_file.name || 'Legislation File',
        url: data?.legislative_file.url,
        size: data?.legislative_file.size * 1024 * 1024,
      },
    ]
    : []

  const publicRecords =
    data?.consultation_records?.map((record, index) => ({
      name: record.public_file?.name || `Public Record ${index + 1}`,
      url: record.public_file?.url,
      size: record.public_file?.size * 1024 * 1024,
    })) || []

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={formTitle} size='5xl'>
      <div className='px-5 py-5 space-y-6 max-h-[70vh] overflow-y-auto'>
        {isLoading && (
          <div className='flex items-center justify-center py-12'>
            <div className='text-center'>
              <div className='inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600'></div>
              <p className='mt-4 text-sm text-gray-500'>
                Loading legislation details...
              </p>
            </div>
          </div>
        )}

        {isError && (
          <div className='flex items-center justify-center py-12'>
            <div className='text-center'>
              <p className='text-sm text-red-500 mb-2'>
                {error?.response?.data?.message ||
                  error?.message ||
                  'Failed to load legislation details.'}
              </p>
              <button
                onClick={onClose}
                className='text-sm text-blue-600 hover:underline'
              >
                Close
              </button>
            </div>
          </div>
        )}

        {!isLoading && !isError && (
          <>
            <div className='mb-6'>
              <p className='text-lg font-bold mb-3'>
                {legislationType} Details
              </p>
              <div className='bg-[#F9FAFB] rounded-lg p-5'>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-6'>
                  <div>
                    <label className='block text-md font-medium text-gray-500 mb-1'>
                      Legislation Type
                    </label>
                    <p className=' text-gray-900'>{legislationType}</p>
                  </div>

                  <div>
                    <label className='block text-md font-medium text-gray-500 mb-1'>
                      Status
                    </label>
                    <span
                      className={`inline-flex px-4 py-1 text-sm font-semibold rounded-full ${getStatusStyles(
                        data?.status
                      )}`}
                    >
                      {status}
                    </span>
                  </div>

                  <div>
                    <label className='block text-md font-medium text-gray-500 mb-1'>
                      Legislation Title
                    </label>
                    <p className=' text-gray-900'>
                      {data?.title || 'Untitled'}
                    </p>
                  </div>

                  <div className=''>
                    <label className='block text-md font-medium text-gray-500 mb-1'>
                      Legislation Number
                    </label>
                    <p className='text-gray-900'>{legislationNumber}</p>
                  </div>
                </div>
                <div>
                  <label className='block text-md font-medium text-gray-500 mb-2'>
                    Description
                  </label>
                  <p className='text-gray-900 leading-relaxed whitespace-pre-wrap'>
                    {structuredFields.description}
                  </p>
                </div>
              </div>
            </div>

            <div className='mb-6'>
              <p className='text-lg font-bold mb-3'>Committee & Chairmanship</p>
              <div className='bg-[#F9FAFB] rounded-lg p-5'>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                  <div>
                    <label className='block text-md font-medium text-gray-500 mb-2'>
                      Committee
                    </label>
                    <p className='text-base text-gray-900'>
                      {structuredFields.committee}
                    </p>
                  </div>

                  <div>
                    <label className='block text-md font-medium text-gray-500 mb-2'>
                      Chairmanship
                    </label>
                    {structuredFields.chairmanship ? (
                      <ProfileBadge
                        initials={getInitials(
                          structuredFields.chairmanship?.name ||
                          structuredFields.chairmanship?.full_name ||
                          structuredFields.chairmanship?.resident?.name ||
                          ''
                        )}
                        name={
                          structuredFields.chairmanship?.name ||
                          structuredFields.chairmanship?.full_name ||
                          structuredFields.chairmanship?.resident?.name ||
                          'N/A'
                        }
                        position={
                          structuredFields.chairmanship?.position ||
                          structuredFields.chairmanship?.role ||
                          structuredFields.chairmanship?.resident?.position ||
                          'Chairperson'
                        }
                      />
                    ) : (
                      <p className='text-base text-gray-500'>
                        No chairmanship assigned
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className='mb-6'>
              <p className='text-lg font-bold mb-3'>Sponsorship & Authors</p>
              <div className='bg-[#F9FAFB] rounded-lg p-5'>
                <label className='block text-sm font-medium text-gray-500 mb-3'>
                  Authors/Main Sponsors
                </label>
                {structuredFields.authors.length > 0 ? (
                  <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3'>
                    {structuredFields.authors.map((author, index) => {
                      const authorName =
                        author?.name ||
                        author?.full_name ||
                        author?.resident?.name ||
                        'N/A'
                      const authorRole =
                        author?.role ||
                        author?.position ||
                        author?.resident?.position ||
                        'Resident'
                      // Format role to be more readable (e.g., "barangay_official" -> "Barangay Official")
                      const authorPosition = formatRole(authorRole)

                      return (
                        <div
                          key={author?.user_id || index}
                          className='flex items-center space-x-3 bg-gray-50 rounded-lg py-3'
                        >
                          <div className='flex-shrink-0'>
                            <div className='w-10 h-10 rounded-full bg-gradient-to-r from-[#60A4F9] to-[#2865EC] flex items-center justify-center text-white text-sm font-semibold'>
                              {getInitials(authorName)}
                            </div>
                          </div>
                          <div className='min-w-0 flex-1'>
                            <p className='text-sm font-medium text-gray-900 truncate'>
                              {authorName}
                            </p>
                            <p className='text-sm text-gray-500 truncate'>
                              {authorPosition}
                            </p>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <p className='text-base text-gray-500'>
                    No authors/sponsors assigned
                  </p>
                )}
              </div>
            </div>
            {type !== "history" && (
              <div>
                <p className='text-lg font-bold mb-3'>Attachments</p>
                <div className='space-y-6'>
                  <FileUploader
                    label='Legislation Document File'
                    inputId='legislation-file'
                    initialFiles={legislationFile}
                    multiple={false}
                    mode='view'
                  />

                  <FileUploader
                    label='Public Records'
                    inputId='public-records'
                    initialFiles={publicRecords}
                    multiple={true}
                    mode='view'
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  )
}

export default ViewLegislationModal
